import { IonAlert, IonicSafeString, IonLoading } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocalStorage } from "@uidotdev/usehooks";
import { jwtDecode } from "jwt-decode";
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

import { $api } from "@/api/api";
import {
  FIVE_SECONDS,
  MILLISECOND_IN_SECOND,
  ONE_MINUTE,
  TEN_MINUTES,
  TEN_SECONDS,
} from "@/utils/constants/time";

const MAX_ERROR_COUNT = 5;

const ALERT_LOGOUT_MESSAGE = new IonicSafeString(
  `<ion-row class="ion-align-items-center">
  <ion-col size="auto">You can wait here or logout and try again.</ion-col>
  <ion-col size="auto"><ion-spinner></ion-spinner></ion-col>
  </ion-row>
  `,
);

const ApiTokenContext = createContext<{
  // Token is valid for 1h, long-term-token is valid for 180 days
  login: (token: string, longTermToken: string) => void;
  unsetToken: () => void;
  logout: () => void;
  isRenewingToken: boolean;
  token: string | false;
}>({
  isRenewingToken: false,
  login: () => {
    //
  },

  logout: () => {
    //
  },
  token: false,
  unsetToken: () => {
    //
  },
});

function isTokenValidFn(token: string | false): boolean {
  try {
    if (token) {
      const tokenData = jwtDecode(token),
        tokenExpire = tokenData.exp;
      if (tokenExpire) {
        return tokenExpire > Date.now() / MILLISECOND_IN_SECOND;
      }
    }
  } catch (_error) {
    console.error("Stored Token is Invalid");
  }
  return false;
}

function isTokenExpiredOrEndingSoon(token: string | false): boolean {
  if (!token) {
    return true;
  }
  try {
    const exp = jwtDecode(token).exp ?? 0;
    return (
      exp - TEN_MINUTES / MILLISECOND_IN_SECOND <
      Date.now() / MILLISECOND_IN_SECOND
    );
  } catch {
    return true;
  }
}

function isLongTermTokenCurrentlyValid(LTToken: string): boolean {
  try {
    const exp = jwtDecode(LTToken).exp ?? 0;
    return exp > Date.now() / MILLISECOND_IN_SECOND;
  } catch {
    return false;
  }
}

// This provider manages token consistency
// It renews short-term tokens before expiration
// eslint-disable-next-line max-lines-per-function
function ApiTokenProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient(),
    [token, setToken] = useLocalStorage<string>("bearer", ""),
    [LTToken, setLTToken] = useLocalStorage<string>("LTToken", ""),
    [nextRefetch, setNextRefetch] = useState<Date>(() => new Date()),
    [errorCount, setErrorCount] = useState(0),
    [visibilityNonce, setVisibilityNonce] = useState(0),
    { mutate, isPending, reset } = $api.useMutation(
      "post",
      "/login/renew-token",
    ),
    isTokenValid = isTokenValidFn(token),
    isLTTokenValid = isTokenValidFn(LTToken),
    [longTimeoutMode, setLongTimeoutMode] = useState(false);

  const logout = useCallback(() => {
    setToken("");
    setLTToken("");
    setErrorCount(0);
    queryClient.clear();
  }, [queryClient, setToken, setLTToken]);

  // Keep the expired bearer token while a valid long-term token exists so renewal
  // can replace it without clearing auth state and forcing a full re-login UI.
  useEffect(() => {
    if (!isTokenValid && token !== "" && !isLTTokenValid) {
      setToken("");
    }
  }, [isLTTokenValid, isTokenValid, token, setToken]);

  useEffect(() => {
    if (!isLTTokenValid && LTToken !== "") {
      setLTToken("");
    }
  }, [isLTTokenValid, LTToken, setLTToken]);

  const requestTokenRenewal = useCallback(() => {
    if (isPending || nextRefetch > new Date()) {
      return;
    }
    if (
      !LTToken ||
      !isLongTermTokenCurrentlyValid(LTToken) ||
      !isTokenExpiredOrEndingSoon(token)
    ) {
      return;
    }

    setNextRefetch(() => new Date(Date.now() + FIVE_SECONDS));
    mutate(
      { params: { header: { longTermToken: LTToken } } },
      {
        onError() {
          setErrorCount((prev) => prev + 1);
        },
        onSettled() {
          reset();
        },
        onSuccess(data) {
          setToken(data.newToken);
          setLongTimeoutMode(false);
          setErrorCount(0);
          queryClient
            .invalidateQueries({ queryKey: ["get", "/login"] })
            .catch(() => {
              // Token is already updated; a stale user profile is acceptable until next fetch.
            });
        },
      },
    );
  }, [
    LTToken,
    isPending,
    mutate,
    nextRefetch,
    queryClient,
    reset,
    setToken,
    token,
  ]);

  // Re-run renewal checks after the cooldown expires (failed attempts retry promptly).
  useEffect(() => {
    const msUntilRefetch = nextRefetch.getTime() - Date.now();
    if (msUntilRefetch <= 0) {
      return () => {
        //
      };
    }
    const timeout = setTimeout(() => {
      setNextRefetch(new Date());
    }, msUntilRefetch);
    return () => clearTimeout(timeout);
  }, [nextRefetch]);

  // Renew when tokens change, when the tab becomes visible again, or on a periodic check.
  useEffect(() => {
    requestTokenRenewal();
  }, [requestTokenRenewal, visibilityNonce]);

  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        // A renewal started while the tab was hidden can remain pending forever;
        // reset it so returning to the app can retry immediately.
        reset();
        setNextRefetch(new Date());
        setVisibilityNonce((value) => value + 1);
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [reset]);

  const isLoginInProgress = isLTTokenValid && !isTokenValid;

  useEffect(() => {
    const intervalMs = isLoginInProgress ? FIVE_SECONDS : ONE_MINUTE;
    const interval = setInterval(requestTokenRenewal, intervalMs);
    return () => clearInterval(interval);
  }, [isLoginInProgress, requestTokenRenewal]);

  useEffect(() => {
    if (errorCount > MAX_ERROR_COUNT) {
      logout();
    }
  }, [errorCount, logout]);

  useEffect(() => {
    // Reset heartbeat text to "Logging you in..." every time the login process starts/finished
    setLongTimeoutMode(false);
    const timeout = setTimeout(() => {
      setLongTimeoutMode(true);
    }, TEN_SECONDS);
    return () => clearTimeout(timeout);
  }, [isLoginInProgress]);

  return (
    <ApiTokenContext.Provider
      value={{
        isRenewingToken: isLoginInProgress,
        login(newToken, ltToken) {
          setToken(newToken);
          setLTToken(ltToken);
          setLongTimeoutMode(false);
        },
        logout,
        token,
        unsetToken() {
          setToken("");
        },
      }}
    >
      {isLoginInProgress && longTimeoutMode && (
        <IonAlert
          isOpen={longTimeoutMode}
          header="Logging you in..."
          subHeader="This seems to be taking a while."
          message={ALERT_LOGOUT_MESSAGE}
          buttons={[
            {
              handler: () => {
                setLongTimeoutMode(() => false);
                logout();
              },
              role: "cancel",
              text: "Logout",
            },
          ]}
        ></IonAlert>
      )}
      {isLoginInProgress && (
        <IonLoading isOpen={!longTimeoutMode} message="Logging you in..." />
      )}
      {children}
    </ApiTokenContext.Provider>
  );
}

export { ApiTokenContext, ApiTokenProvider };
